// telDebug.js — 通话页现场取证日志
//
// 默认对所有人完全静默：只把 console / 全局错误 / fetch / XHR / WebSocket
// 采集进内存环形缓冲（无界面、无网络上报、不改任何拨号行为）。
//
// 日志面板的打开方式（只告诉指定测试人）：
//   1. 在「正在呼叫/昵称」信息区快速连点 5 下 → 弹出/收起面板；
//   2. URL 带 telDebug=1 → 进页自动弹出（浏览器里验证部署用）。
//
// 两个防"日志随页面销毁而丢失"的机制：
//   - holdForCopy：面板开着时，页面挂断/失败后不会立刻被 RN 关掉，出现「复制完了，关闭页面」
//     按钮，复制完再放行（超时 120s 自动放行）；面板没开的普通用户完全不受影响。
//   - 遗言备份：日志实时备份到 localStorage，下次进页自动恢复上次残留（iOS 的 WebView 是
//     incognito 存不住，安卓有效）。
//
// 所有钩子都包 try/catch：采集本身出任何异常，原逻辑照常执行。

const MAX_LINES = 1000
const TAP_COUNT = 5
const TAP_WINDOW = 2000 // 连点判定窗口(ms)
const LS_LOG_KEY = 'tel_debug_last_log' // 遗言备份（安卓有效）
const HOLD_TIMEOUT = 120000 // 暂缓关页的兜底时长

let installed = false
let buffer = [] // {time, level, text}
let restoredCount = 0 // 上次会话恢复的行数（备份时跳过，避免套娃累积）
let persistTimer = null
let tapTimes = []
let ui = null // {root, logEl, hintEl}
let holdBar = null // 暂缓关页的提示条

// ---------- 基础工具 ----------
function pad(n) {
  return n < 10 ? '0' + n : '' + n
}

function ts() {
  const d = new Date()
  return pad(d.getHours()) + ':' + pad(d.getMinutes()) + ':' + pad(d.getSeconds()) + '.' + ('00' + d.getMilliseconds()).slice(-3)
}

function short(s, n) {
  s = String(s)
  n = n || 300
  return s.length > n ? s.slice(0, n) + '…' : s
}

function safeStr(a) {
  if (typeof a === 'string') return a
  try {
    return JSON.stringify(a)
  } catch (e) {
    return String(a)
  }
}

// 处理 console 的 %c/%s/%d 等格式符：%c 对应的 CSS 参数丢掉，只留文字/数据
function formatConsole(argsLike) {
  const args = Array.prototype.slice.call(argsLike)
  if (args.length && typeof args[0] === 'string' && args[0].indexOf('%') !== -1) {
    const rest = args.slice(1)
    let i = 0
    const out = args[0].replace(/%[csdifoOj%]/g, spec => {
      if (spec === '%%') return '%'
      if (spec === '%c') {
        i++
        return ''
      }
      const a = rest[i++]
      return a === undefined ? spec : safeStr(a)
    })
    const extra = rest.slice(i).map(safeStr)
    return (out + (extra.length ? ' ' + extra.join(' ') : '')).replace(/[ \t]{2,}/g, ' ').trim()
  }
  return args.map(safeStr).join(' ')
}

// 页面 URL 里带坐席 token，记录时打码
function maskedHref() {
  try {
    return window.location.href.replace(/([?&]token=)[^&]*/, '$1***')
  } catch (e) {
    return ''
  }
}

const LEVEL_COLOR = {
  time: '#8a93a2',
  info: '#c7cdd6',
  req: '#e0b35a',
  resp: '#6fa8ff',
  event: '#b48cff',
  ok: '#5fbf77',
  err: '#ff6b6b',
}

function push(level, text) {
  try {
    const line = { time: ts(), level: level, text: text }
    buffer.push(line)
    if (buffer.length > MAX_LINES) buffer.shift()
    if (ui && ui.root.style.display !== 'none') appendLine(line)
    schedulePersist()
  } catch (e) {}
}

// 遗言备份：1.5s 节流写入 localStorage，只存本次会话的行
function schedulePersist() {
  if (persistTimer) return
  persistTimer = setTimeout(() => {
    persistTimer = null
    try {
      window.localStorage.setItem(LS_LOG_KEY, JSON.stringify(buffer.slice(restoredCount).slice(-300)))
    } catch (e) {}
  }, 1500)
}

// 进页时恢复上次会话残留（页面被 RN 销毁前没来得及复制的部分）
function restoreLastLog() {
  try {
    const prev = window.localStorage.getItem(LS_LOG_KEY)
    if (!prev) return
    const lines = JSON.parse(prev)
    if (!lines || !lines.length) return
    buffer.push({ time: '--:--:--.---', level: 'req', text: '══════ 上次会话残留日志 ' + lines.length + ' 行 ══════' })
    for (let i = 0; i < lines.length; i++) buffer.push(lines[i])
    buffer.push({ time: '--:--:--.---', level: 'req', text: '══════ 上次残留结束，以下为本次会话 ══════' })
    restoredCount = buffer.length
  } catch (e) {}
}

// ---------- 采集钩子（照搬已验证过的 dezhu-dial-debug 页面那套） ----------
function installTaps() {
  // console
  ;['log', 'info', 'warn', 'error'].forEach(k => {
    try {
      const orig = console[k] ? console[k].bind(console) : function () {}
      console[k] = function () {
        try {
          push(k === 'error' ? 'err' : k === 'warn' ? 'req' : 'info', '[console.' + k + '] ' + short(formatConsole(arguments), 400))
        } catch (e) {}
        return orig.apply(console, arguments)
      }
    } catch (e) {}
  })

  // 未捕获错误 / 资源加载失败 / Promise
  try {
    window.addEventListener(
      'error',
      e => {
        let d = (e && e.message) || ''
        if (e && e.target && e.target.tagName) d = e.target.tagName + ' 资源加载失败 ' + short(e.target.src || e.target.href || '', 120)
        if (d) push('err', '[error] ' + short(d, 300))
      },
      true
    )
    window.addEventListener('unhandledrejection', e => {
      push('err', '[promise] ' + short((e && e.reason) || '', 300))
    })
  } catch (e) {}

  // fetch（RN 注入的包装先于本模块生效，这里包在最外层，互不影响）
  try {
    const origFetch = window.fetch
    if (origFetch) {
      window.fetch = function (input, init) {
        let u = ''
        let m = 'GET'
        try {
          const raw = typeof input === 'string' ? input : (input && input.url) || ''
          u = short(raw.replace(/^https?:\/\//, ''), 100)
          m = (init && init.method) || (input && input.method) || 'GET'
        } catch (e) {}
        return origFetch.apply(this, arguments).then(
          res => {
            push(res.ok ? 'resp' : 'err', '[fetch] ' + m + ' ' + u + ' → ' + res.status)
            return res
          },
          err => {
            push('err', '[fetch] ' + m + ' ' + u + ' ✗ ' + short(err, 120))
            throw err
          }
        )
      }
    }
  } catch (e) {}

  // XHR（含被 RN PROXY_XHR 代理的请求：代理回调会派发 loadend，一样能记到状态码）
  try {
    const oo = XMLHttpRequest.prototype.open
    const os = XMLHttpRequest.prototype.send
    XMLHttpRequest.prototype.open = function (m, u) {
      this._td_m = m
      this._td_u = u
      return oo.apply(this, arguments)
    }
    XMLHttpRequest.prototype.send = function () {
      const self = this
      const u = short(String(self._td_u || '').replace(/^https?:\/\//, ''), 100)
      try {
        self.addEventListener('loadend', function () {
          push(self.status >= 200 && self.status < 300 ? 'resp' : 'err', '[xhr] ' + (self._td_m || '') + ' ' + u + ' → ' + (self.status || 'ERR'))
        })
      } catch (e) {}
      return os.apply(this, arguments)
    }
  } catch (e) {}

  // WebSocket（SIP 信令通道的建立/断开是关键证据）
  try {
    const OW = window.WebSocket
    if (OW) {
      const W = function WebSocket(url, protos) {
        push('info', '[ws] 连接 ' + short(url, 100))
        const ws = protos !== undefined ? new OW(url, protos) : new OW(url)
        try {
          ws.addEventListener('open', () => {
            push('ok', '[ws] open ' + short(url, 60))
          })
          ws.addEventListener('close', ev => {
            push('err', '[ws] close code=' + ev.code + (ev.reason ? ' ' + ev.reason : '') + ' ' + short(url, 60))
          })
          ws.addEventListener('error', () => {
            push('err', '[ws] error ' + short(url, 60))
          })
        } catch (e) {}
        return ws
      }
      W.prototype = OW.prototype
      W.CONNECTING = OW.CONNECTING
      W.OPEN = OW.OPEN
      W.CLOSING = OW.CLOSING
      W.CLOSED = OW.CLOSED
      window.WebSocket = W
    }
  } catch (e) {}
}

// ---------- 面板（纯 DOM，不进 React 渲染树） ----------
function appendLine(line) {
  try {
    const el = document.createElement('div')
    const t = document.createElement('span')
    t.style.color = LEVEL_COLOR.time
    t.textContent = line.time + ' '
    const m = document.createElement('span')
    m.style.color = LEVEL_COLOR[line.level] || LEVEL_COLOR.info
    m.textContent = line.text
    el.appendChild(t)
    el.appendChild(m)
    ui.logEl.appendChild(el)
    while (ui.logEl.childNodes.length > MAX_LINES) ui.logEl.removeChild(ui.logEl.firstChild)
    ui.logEl.scrollTop = ui.logEl.scrollHeight
  } catch (e) {}
}

function setHint(text) {
  if (!ui) return
  ui.hintEl.textContent = text
  setTimeout(() => {
    if (ui) ui.hintEl.textContent = ''
  }, 2500)
}

function dumpText() {
  return buffer.map(l => l.time + ' [' + l.level + '] ' + l.text).join('\n')
}

function copyAll() {
  const txt = dumpText()
  const done = () => setHint('已复制 ' + buffer.length + ' 行，粘贴发给技术')
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(txt).then(done, () => copyFallback(txt, done))
      return
    }
  } catch (e) {}
  copyFallback(txt, done)
}

function copyFallback(txt, done) {
  try {
    const ta = document.createElement('textarea')
    ta.value = txt
    ta.style.position = 'fixed'
    ta.style.left = '-9999px'
    document.body.appendChild(ta)
    ta.select()
    document.execCommand('copy')
    document.body.removeChild(ta)
    done()
  } catch (e) {
    setHint('复制失败，请截图日志区')
  }
}

// 日志太长微信发不出时用：按 3000 字切段，点一次复制一段
const SEG_SIZE = 3000
let segIndex = 0

function copySegmented() {
  const txt = dumpText()
  const total = Math.max(1, Math.ceil(txt.length / SEG_SIZE))
  if (segIndex >= total) segIndex = 0
  const part = '【日志第' + (segIndex + 1) + '/' + total + '段】\n' + txt.slice(segIndex * SEG_SIZE, (segIndex + 1) * SEG_SIZE)
  const cur = segIndex
  segIndex++
  const done = () => setHint('已复制第 ' + (cur + 1) + '/' + total + ' 段，继续点直到发完')
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(part).then(done, () => copyFallback(part, done))
      return
    }
  } catch (e) {}
  copyFallback(part, done)
}

function makeBtn(label, onClick, primary) {
  const b = document.createElement('span')
  b.textContent = label
  b.style.cssText = primary
    ? 'padding:8px 18px;margin-left:8px;border:1px solid #2f6fed;border-radius:8px;background:#2f6fed;color:#fff;font-size:15px;font-weight:600;'
    : 'padding:4px 10px;margin-left:8px;border:1px solid #5a6472;border-radius:6px;color:#dde3ea;font-size:12px;'
  b.onclick = onClick
  return b
}

function showPanel() {
  try {
    if (ui) {
      // 收起期间的新日志只在内存里，重新打开时全量重绘
      ui.logEl.innerHTML = ''
      ui.root.style.display = 'flex'
      buffer.forEach(appendLine)
      return
    }
    // 贴底部展示（顶部会被刘海挡按钮）；盖住「结束」时连点5下信息区可收起
    const root = document.createElement('div')
    root.style.cssText =
      'position:fixed;left:0;right:0;bottom:0;height:46%;z-index:999999;display:flex;flex-direction:column;' +
      'background:rgba(10,12,16,0.94);border-top:1px solid #39414d;font-family:Menlo,Consolas,monospace;'

    const head = document.createElement('div')
    head.style.cssText = 'display:flex;align-items:center;flex-wrap:wrap;row-gap:6px;padding:6px 10px;flex:none;'
    const title = document.createElement('span')
    title.textContent = '通话调试日志'
    title.style.cssText = 'color:#9aa3af;font-size:12px;flex:1;'
    const hint = document.createElement('span')
    hint.style.cssText = 'color:#5fbf77;font-size:11px;margin-right:4px;'

    const logEl = document.createElement('div')
    logEl.style.cssText =
      'flex:1;overflow:auto;padding:6px 10px;font-size:11px;line-height:1.5;white-space:pre-wrap;word-break:break-all;' +
      '-webkit-overflow-scrolling:touch;padding-bottom:max(env(safe-area-inset-bottom),10px);'

    head.appendChild(title)
    head.appendChild(hint)
    head.appendChild(makeBtn('复制全部', copyAll, true))
    head.appendChild(makeBtn('分段', copySegmented))
    head.appendChild(
      makeBtn('清空', () => {
        buffer = []
        logEl.innerHTML = ''
      })
    )
    head.appendChild(
      makeBtn('关闭', () => {
        root.style.display = 'none'
      })
    )
    root.appendChild(head)
    root.appendChild(logEl)
    document.body.appendChild(root)

    ui = { root: root, logEl: logEl, hintEl: hint }
    buffer.forEach(appendLine)
  } catch (e) {}
}

function shouldAutoOpen() {
  try {
    return /([?&])telDebug=1/.test(window.location.search)
  } catch (e) {
    return false
  }
}

function isPanelOpen() {
  try {
    return !!(ui && ui.root.style.display !== 'none')
  } catch (e) {
    return false
  }
}

// 面板开着时由 Tel 页调用：页面即将被 RN 关闭前，弹出确认条暂缓关闭，
// 复制完点「复制完了，关闭页面」或超时后调用 releaseCb 放行
function holdForCopy(releaseCb) {
  try {
    if (!isPanelOpen()) showPanel()
    let released = false
    const release = () => {
      if (released) return
      released = true
      try {
        if (holdBar && holdBar.parentNode) holdBar.parentNode.removeChild(holdBar)
      } catch (e) {}
      holdBar = null
      releaseCb()
    }
    if (holdBar) {
      release()
      return
    }
    holdBar = document.createElement('div')
    holdBar.style.cssText = 'display:flex;align-items:center;padding:8px 10px;background:#3a2b12;border-bottom:1px solid #6b4e1c;flex:none;'
    const txt = document.createElement('span')
    txt.textContent = '通话已结束：页面暂不关闭，先复制日志'
    txt.style.cssText = 'color:#e0b35a;font-size:12px;flex:1;'
    const btn = document.createElement('span')
    btn.textContent = '复制完了，关闭页面'
    btn.style.cssText = 'padding:8px 14px;margin-left:8px;border-radius:8px;background:#c0392b;color:#fff;font-size:14px;font-weight:600;flex:none;'
    btn.onclick = release
    holdBar.appendChild(txt)
    holdBar.appendChild(btn)
    ui.root.insertBefore(holdBar, ui.root.firstChild)
    setTimeout(release, HOLD_TIMEOUT)
    push('req', '[telDebug] 已暂停页面关闭等待复制（最长 ' + HOLD_TIMEOUT / 1000 + ' 秒）')
  } catch (e) {
    try {
      releaseCb()
    } catch (e2) {}
  }
}

// ---------- 对外接口 ----------
function install() {
  if (installed) return
  installed = true
  restoreLastLog()
  try {
    installTaps()
  } catch (e) {}
  push('info', '[telDebug] 采集已启动 origin=' + window.location.origin)
  push('info', '[telDebug] ua=' + navigator.userAgent)
  push('info', '[telDebug] url=' + maskedHref())
  if (shouldAutoOpen()) showPanel()
}

// 挂在信息区 onClick 上：TAP_WINDOW 内连点 TAP_COUNT 下，开/关面板切换
function handleTap() {
  try {
    const now = Date.now()
    tapTimes = tapTimes.filter(t => now - t < TAP_WINDOW)
    tapTimes.push(now)
    if (tapTimes.length >= TAP_COUNT) {
      tapTimes = []
      if (ui && ui.root.style.display !== 'none') {
        ui.root.style.display = 'none'
      } else {
        showPanel()
        push('info', '[telDebug] 面板已通过连点手势打开')
      }
    }
  } catch (e) {}
}

export default { install, handleTap, isPanelOpen, holdForCopy }



// WEBPACK FOOTER //
// ./src/modules/tel/telDebug.js